import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InvoicingMonthSettingsRow, InvoicingSettingsService } from '../../../core/services/invoicing-settings.service';
import { InvoicingSettingsApiService, InvoicingSettingsMonthDto } from '../../../core/services/invoicing-settings-api.service';

@Component({
  selector: 'em-invoicing-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    InputNumberModule,
    ButtonModule,
    ToastModule,
    TranslateModule,
  ],
  templateUrl: './invoicing-settings.component.html',
  styleUrl: './invoicing-settings.component.scss',
})
export class InvoicingSettingsComponent implements OnInit {
  currentYear = new Date().getFullYear();
  rows: InvoicingMonthSettingsRow[] = [];
  private initialSnapshot = '';
  loading = false;
  rowErrors: Record<string, string> = {};
  translations: Record<string, string> = {};

  constructor(
    private readonly invoicingSettings: InvoicingSettingsService,
    private readonly invoicingSettingsApi: InvoicingSettingsApiService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.loadTranslations();
    this.loadYearData();
  }

  private loadTranslations(): void {
    this.translate.get([
      'layout.configurations.invoicingSettings.title',
      'layout.configurations.invoicingSettings.save',
      'layout.configurations.invoicingSettings.loading',
      'layout.configurations.invoicingSettings.noChanges.title',
      'layout.configurations.invoicingSettings.noChanges.detail',
      'layout.configurations.invoicingSettings.validationError.title',
      'layout.configurations.invoicingSettings.validationMessage',
      'layout.configurations.invoicingSettings.saveSuccess.title',
      'layout.configurations.invoicingSettings.saveSuccess.detail',
      'layout.configurations.invoicingSettings.saveError.title',
      'layout.configurations.invoicingSettings.saveError.detail',
      'layout.configurations.invoicingSettings.loadError.title',
      'layout.configurations.invoicingSettings.loadError.detail',
    ]).subscribe((translations) => {
      this.translations = translations;
    });
  }

  private loadYearData(): void {
    this.invoicingSettingsApi.getYear(this.currentYear).subscribe({
      next: (backendRows) => {
        this.invoicingSettings.setYearFromBackendRows(this.currentYear, backendRows);
        this.rows = this.invoicingSettings.getYearRows(this.currentYear, this.translate.currentLang);
        this.initialSnapshot = this.snapshotRows(this.rows);
        this.loading = false;
      },
      error: () => {
        this.invoicingSettings.ensureMigratedForYear(this.currentYear);
        this.rows = this.invoicingSettings.getYearRows(this.currentYear, this.translate.currentLang);
        this.initialSnapshot = this.snapshotRows(this.rows);
        this.loading = false;

        this.messageService.add({
          severity: 'warn',
          summary: this.translations['layout.configurations.invoicingSettings.loadError.title'],
          detail: this.translations['layout.configurations.invoicingSettings.loadError.detail'],
        });
      },
    });
  }

  get hasChanges(): boolean {
    return this.snapshotRows(this.rows) !== this.initialSnapshot;
  }

  get hasValidationErrors(): boolean {
    return Object.keys(this.rowErrors).length > 0;
  }

  onRowEdit(row: InvoicingMonthSettingsRow): void {
    this.validateRow(row);
  }

  save(): void {
    if (!this.hasChanges) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translations['layout.configurations.invoicingSettings.noChanges.title'],
        detail: this.translations['layout.configurations.invoicingSettings.noChanges.detail'],
      });
      return;
    }

    this.validateAllRows();
    if (this.hasValidationErrors) {
      const firstInvalid = this.rows.find((r) => !!this.rowErrors[r.key]);
      const firstLabel = firstInvalid?.label ?? '';
      const firstMsg = firstInvalid ? this.rowErrors[firstInvalid.key] : '';
      this.messageService.add({
        severity: 'error',
        summary: this.translations['layout.configurations.invoicingSettings.validationError.title'],
        detail: `${firstLabel}: ${firstMsg}`,
      });
      return;
    }

    const payload: InvoicingSettingsMonthDto[] = this.rows.map((r) => ({
      year: this.currentYear,
      month: Number.parseInt(r.key.split('-')[1] ?? '1', 10),
      forecastMonths: r.forecastMonths,
      closingMonths: r.closingMonths,
      regenerateMonths: r.regenerateMonths,
      active: true,
    }));

    this.loading = true;
    this.invoicingSettingsApi.upsertYear(this.currentYear, payload).subscribe({
      next: (saved) => {
        this.invoicingSettings.setYearFromBackendRows(this.currentYear, saved);
        this.rows = this.invoicingSettings.getYearRows(this.currentYear, this.translate.currentLang);
        this.initialSnapshot = this.snapshotRows(this.rows);
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.invoicingSettings.saveSuccess.title'],
          detail: this.translations['layout.configurations.invoicingSettings.saveSuccess.detail'],
        });
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.invoicingSettings.saveError.title'],
          detail: this.translations['layout.configurations.invoicingSettings.saveError.detail'],
        });
      },
    });
  }

  private snapshotRows(rows: InvoicingMonthSettingsRow[]): string {
    return JSON.stringify(rows.map((r) => ({
      key: r.key,
      forecastMonths: r.forecastMonths,
      closingMonths: r.closingMonths,
      regenerateMonths: r.regenerateMonths,
    })));
  }

  private validateAllRows(): void {
    this.rowErrors = {};
    this.rows.forEach((row) => this.validateRow(row));
  }

  private validateRow(row: InvoicingMonthSettingsRow): void {
    const message =
      row.forecastMonths > row.regenerateMonths
        ? this.translate.instant('layout.configurations.invoicingSettings.validationMessage', {
            forecast: row.forecastMonths,
            regenerate: row.regenerateMonths,
          })
        : null;

    if (message) {
      this.rowErrors[row.key] = message;
    } else if (this.rowErrors[row.key]) {
      delete this.rowErrors[row.key];
    }
  }
}
